import { afterNextRender, Component, inject, Injector, input, output, signal, viewChild } from '@angular/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { QuickAddMenuOption } from '../models/calendar.model';

/**
 * Shared cursor-anchored quick-add menu for `CalendarComponent` and
 * `ResourceDayCalendarComponent`: an invisible anchor positioned at the
 * click point plus a `mat-menu` listing the given options. Each parent owns
 * its own option list (they differ per mode/column) and calls `open()` with
 * the anchor position; this component only owns the menu's open/close
 * mechanics.
 */
@Component({
  selector: 'siat-quick-add-menu',
  imports: [MatMenuModule],
  template: `
    <span
      #trigger="matMenuTrigger"
      style="position: fixed; width: 0; height: 0; pointer-events: none"
      [style.left.px]="anchorPosition().x"
      [style.top.px]="anchorPosition().y"
      [matMenuTriggerFor]="menu"
    ></span>
    <mat-menu #menu="matMenu" class="min-w-65!" (closed)="closed.emit()">
      @for (option of options(); track option.action) {
        <button mat-menu-item (click)="optionSelected.emit(option.action)">
          <span aria-hidden="true">{{ option.icon }}</span>
          {{ option.label }}
        </button>
      }
    </mat-menu>
  `,
})
export class QuickAddMenuComponent {
  /**
   * The options to list in the menu.
   */
  options = input<QuickAddMenuOption[]>([]);

  /**
   * Emitted with the clicked option's action.
   */
  optionSelected = output<string>();

  /**
   * Emitted when the menu closes (e.g. to clear a placeholder event).
   */
  closed = output<void>();

  /**
   * The menu's anchor position, in viewport pixels.
   */
  protected readonly anchorPosition = signal<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  private readonly trigger = viewChild<MatMenuTrigger>('trigger');
  private readonly injector = inject(Injector);

  /**
   * Positions the anchor and opens the menu on the next render.
   */
  open(position: { x: number; y: number }): void {
    this.anchorPosition.set(position);
    afterNextRender(() => this.trigger()?.openMenu(), {
      injector: this.injector,
    });
  }
}
